import { useToast } from '@chakra-ui/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState
} from 'react'

import { cloneDataInstance } from '@/modules/copyDataDriveToClient'
import { CloneFilesDriveGroup } from '@/modules/copyDataDriveToClient/domain/models/CloneFilesDriveGroup'
import { patientInstance } from '@/modules/patient'
import { Patient } from '@/modules/patient/domain/models/Patient'

import { useMultiSelectionContext } from '../hoc/MultiSelection'
import { FileRequestToUpload } from '../hooks/useFileUpload'
import { searchFilter } from '../utils/searchFilter'
import { useAuthContext } from './AuthContext'

const DriveCopyDataContext = createContext<{
  patients: Patient[]
  isLoading: boolean
  patientId: string
  itemsSelected: FileRequestToUpload[]
  parentFolderId: string | null
  searchTerm: string
  isUniqueSendFileDrive: boolean
  isDropOpen: boolean
  uniqueFileDrive: FileRequestToUpload[]
  folderId: string | null
  isOpenModalFolder: boolean
  setParentFolderId: (folderId: string | null) => void
  onOpenModalFolder: (status: boolean) => void
  setFolderId: (folderId: string | null) => void
  sendToDrive: (filesDrive: FileRequestToUpload[]) => void
  setPatientId: (patientId: string) => void
  setPatients: (patients: Patient[]) => void
  setSearchTerm: (searchTerm: string) => void
  setSelectedItems: (items: FileRequestToUpload[]) => void
  setDropOpen: (value: boolean) => void
  setIsUniqueSendFileDrive: (target: boolean) => void
  setUniqueFileDrive: (file: FileRequestToUpload[]) => void
  onCloseModalCloneDataDrive: () => void
}>({
  patients: [],
  isLoading: false,
  patientId: '',
  searchTerm: '',
  itemsSelected: [],
  isDropOpen: false,
  isUniqueSendFileDrive: false,
  uniqueFileDrive: [],
  folderId: null,
  isOpenModalFolder: false,
  parentFolderId: '',
  setParentFolderId: () => {},
  onOpenModalFolder: () => {},
  setFolderId: () => {},
  setUniqueFileDrive: () => {},
  setIsUniqueSendFileDrive: () => {},
  setDropOpen: () => {},
  sendToDrive: () => {},
  setSelectedItems: () => {},
  setPatientId: () => {},
  setPatients: () => {},
  setSearchTerm: () => {},
  onCloseModalCloneDataDrive: () => {}
})

export const useDriveCloneDataContext = () => useContext(DriveCopyDataContext)

export const CopyDataDriveProvider = ({
  children
}: {
  children: ReactNode
}) => {
  const toast = useToast()

  const { turnOffSelection } = useMultiSelectionContext()
  const { personaSession } = useAuthContext()
  const personaId = personaSession?.personaId
  const [searchTerm, setSearchTerm] = useState('')
  const [patientId, setPatientId] = useState<string>('')
  const [isDropOpen, setDropOpen] = useState<boolean>(false)
  const [itemsSelected, setSelectedItems] = useState<FileRequestToUpload[]>([])
  const [isUniqueSendFileDrive, setIsUniqueSendFileDrive] = useState(false)
  const [uniqueFileDrive, setUniqueFileDrive] = useState<FileRequestToUpload[]>(
    []
  )
  const [folderId, setFolderId] = useState<string | null>(null)
  const [isOpenModalFolder, setIsOpenModalFolder] = useState(false)
  const [parentFolderId, setParentFolderId] = useState<string | null>(null)
  const patientsQuery = useQuery({
    queryKey: ['patients', personaId],
    queryFn: async () =>
      (await patientInstance.getTherapistPatientList(personaId!)).filter(
        ({ active }) => active
      ),
    initialData: [],
    enabled: !!personaId
  })

  useEffect(() => {
    const filteredPatients = searchFilter({
      search: searchTerm,
      data: patientsQuery.data,
      filters: [(data) => data.name]
    })

    setPatients(filteredPatients)
  }, [searchTerm])

  useEffect(() => {
    setPatients(patientsQuery.data)
  }, [patientsQuery.data])

  async function sendToDrive(filesDrive: FileRequestToUpload[]) {
    const results = await Promise.allSettled(
      filesDrive.map((file) => {
        const data = {
          fileName: file.originalFileName ?? file.fileName,
          contentType: file.fileContentType,
          fileUrl: file.uploadUrls[0],
          folderId: folderId
        }
        return copyDataToDrivePatient.mutate(data)
      })
    )
    const hasErrors = results.some((result) => result.status === 'rejected')
    if (hasErrors) {
      results.forEach((result) => {
        if (result.status === 'rejected') {
          toast({
            title: 'Erro ao enviar arquivos',
            status: 'error',
            duration: 3000,
            isClosable: true
          })
        }
      })
    } else {
      toast({
        title: 'Arquivos enviados com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true
      })
    }
    !isUniqueSendFileDrive && turnOffSelection()
    setIsUniqueSendFileDrive(false)
  }

  const copyDataToDrivePatient = useMutation({
    mutationKey: ['copyDataDrive', patientId],
    mutationFn: async (data: CloneFilesDriveGroup) => {
      const result = await cloneDataInstance.cloneDataDrive.cloneDataDrive(
        data,
        patientId
      )
      return result
    }
  })

  const [patients, setPatients] = useState<Patient[]>(patientsQuery.data)

  function onCloseModalCloneDataDrive() {
    setDropOpen(false)
    setIsUniqueSendFileDrive(false)
  }
  return (
    <DriveCopyDataContext.Provider
      value={{
        parentFolderId,
        setParentFolderId,
        patients,
        patientId,
        isLoading: patientsQuery.isLoading,
        searchTerm,
        itemsSelected,
        isDropOpen,
        isUniqueSendFileDrive: isUniqueSendFileDrive,
        uniqueFileDrive,
        folderId,
        isOpenModalFolder,
        onOpenModalFolder: (status) => setIsOpenModalFolder(status),
        setFolderId,
        setUniqueFileDrive,
        setIsUniqueSendFileDrive,
        setDropOpen,
        setPatients,
        setPatientId,
        sendToDrive: (filesDrive: FileRequestToUpload[]) =>
          sendToDrive(filesDrive),
        setSelectedItems,
        setSearchTerm,
        onCloseModalCloneDataDrive
      }}
    >
      {children}
    </DriveCopyDataContext.Provider>
  )
}
